import React, { useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  Plus,
  Search,
  Grid,
  List,
  Upload,
  ChevronDown,
  Image as ImageIcon,
  Video,
  AlertCircle,
  Database,
  Users
} from 'lucide-react';
import { useMidias } from '../../hooks/useMidias';
import { MediaPreview } from '../../types/api';
import { Button, Input, Card } from '../../styles/styled-components';
import MediaCard from '../../components/Midias/MediaCard';
import MediaUpload from '../../components/Midias/MediaUpload';
import MediaEdit from '../../components/Midias/MediaEdit';
import MediaDeleteModal from '../../components/Midias/MediaDeleteModal';
import { useCurrentUserId } from '../../hooks/useCurrentUserId';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PageTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

// Grid 2x2 para estatísticas (padrão LoopRA)
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  background: ${({ $color }) => `${$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: ${({ theme }) => theme.spacing[3]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing[2]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: stretch;
    gap: ${({ theme }) => theme.spacing[2]};

    > * {
      flex: 1;
    }
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  min-width: 200px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: none;
    min-width: 0;
    flex: 1 1 100%;
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing[10]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textTertiary};
  pointer-events: none;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    gap: ${({ theme }) => theme.spacing[2]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CustomSelectButton = styled.button`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  white-space: nowrap;
  min-width: fit-content;

  &:focus {
    outline: none;
    border-color: #FF2B2B;
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin: ${({ theme }) => theme.spacing[1]} 0 0 0;
  padding: 0;
  list-style: none;
  z-index: 100;
  max-height: 220px;
  overflow-y: auto;
`;

const DropdownItem = styled.li<{ $active?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  cursor: pointer;
  background: transparent;

  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.borders.radius.md};
    border-top-right-radius: ${({ theme }) => theme.borders.radius.md};
  }

  &:last-child {
    border-bottom-left-radius: ${({ theme }) => theme.borders.radius.md};
    border-bottom-right-radius: ${({ theme }) => theme.borders.radius.md};
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: 4px;
  gap: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const ViewButton = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  border: none;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ $active, theme }) => $active ? 'white' : theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.backgroundTertiary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex: 1;
  }
`;

const AddMediaButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.neon};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    justify-content: center;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const MediaGrid = styled.div<{ $viewMode: 'grid' | 'list' }>`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};

  ${({ $viewMode, theme }) => {
    if ($viewMode === 'grid') {
      return `
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));

        @media (max-width: ${theme.breakpoints.md}) {
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        }

        @media (max-width: ${theme.breakpoints.sm}) {
          grid-template-columns: 1fr;
        }
      `;
    } else {
      return `
        grid-template-columns: 1fr;
      `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${({ theme }) => theme.spacing[4]} auto;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const EmptyStateDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[12]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[12]};
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
`;

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'image' | 'video';

const Midias: React.FC = () => {
  const { userId, loading: userLoading, isLoggedIn } = useCurrentUserId();
  const scopedUserId = userId ?? undefined;

  const {
    mediaPreviews,
    statistics,
    isLoadingMidias,
    mediasError,
    reloadConfigurations
  } = useMidias(scopedUserId);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [showUpload, setShowUpload] = useState(false);
  const [editingMedia, setEditingMedia] = useState<MediaPreview | null>(null);
  const [deletingMedia, setDeletingMedia] = useState<MediaPreview | null>(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const canManageMedia = Boolean(scopedUserId);

  // Fechar dropdowns ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filtrar e ordenar mídias
  const filteredAndSortedMidias = useMemo(() => {
    let filtered = mediaPreviews;

    // Filtrar por termo de busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(media =>
        media.fileName.toLowerCase().includes(searchLower) ||
        media.descricao.toLowerCase().includes(searchLower) ||
        media.palavrasChave.some(keyword => keyword.toLowerCase().includes(searchLower))
      );
    }

    // Filtrar por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(media => media.tipo === filterType);
    }

    // Ordenar sempre por nome para consistência
    const sorted = [...filtered].sort((a, b) => a.fileName.localeCompare(b.fileName));

    return sorted;
  }, [mediaPreviews, searchTerm, filterType]);

  const handleEditMedia = (media: MediaPreview) => {
    setEditingMedia(media);
  };

  const handleDeleteMedia = (media: MediaPreview) => {
    setDeletingMedia(media);
  };

  const handlePreviewMedia = (media: MediaPreview) => {
    if (!media.url) {
      console.warn('[Midias] Preview indisponível para', media.fileName);
      return;
    }

    window.open(media.url, '_blank', 'noopener,noreferrer');
  };

  if (userLoading) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingSpinner />
          <p>Carregando suas mídias...</p>
        </LoadingState>
      </PageContainer>
    );
  }

  if (!isLoggedIn) {
    return (
      <PageContainer>
        <EmptyState>
          <EmptyStateIcon>
            <Users size={40} />
          </EmptyStateIcon>
          <EmptyStateTitle>Acesse sua conta</EmptyStateTitle>
          <EmptyStateDescription>
            Conecte-se com sua conta para enviar e gerenciar seus arquivos de mídia de forma segura.
          </EmptyStateDescription>
        </EmptyState>
      </PageContainer>
    );
  }

  if (isLoadingMidias) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingSpinner />
          <p>Carregando configurações de mídia...</p>
        </LoadingState>
      </PageContainer>
    );
  }

  if (mediasError) {
    return (
      <PageContainer>
        <ErrorState>
          <AlertCircle size={48} />
          <h3>Erro ao carregar mídias</h3>
          <p>Não foi possível carregar as configurações de mídia.</p>
          <Button onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header padrão LoopRA */}
      <PageHeader>
        <PageTitle>Configuração de Mídias</PageTitle>
        <PageSubtitle>
          Gerencie imagens e vídeos que o bot envia automaticamente baseado em palavras-chave
        </PageSubtitle>
      </PageHeader>

      {/* Grid 2x2 para estatísticas - Padrão LoopRA */}
      <StatsGrid>
        <StatCard>
          <StatContent>
            <StatValue>{statistics.total_midias}</StatValue>
            <StatLabel>Total de Mídias</StatLabel>
          </StatContent>
          <StatIcon $color="#ef4444">
            <Database size={24} />
          </StatIcon>
        </StatCard>

        <StatCard>
          <StatContent>
            <StatValue>{statistics.imagens_count}</StatValue>
            <StatLabel>Imagens</StatLabel>
          </StatContent>
          <StatIcon $color="#f97316">
            <ImageIcon size={24} />
          </StatIcon>
        </StatCard>

        <StatCard>
          <StatContent>
            <StatValue>{statistics.videos_count}</StatValue>
            <StatLabel>Vídeos</StatLabel>
          </StatContent>
          <StatIcon $color="#dc2626">
            <Video size={24} />
          </StatIcon>
        </StatCard>

        <StatCard>
          <StatContent>
            <StatValue>{statistics.usuarios_ativos}</StatValue>
            <StatLabel>Usuários Ativos</StatLabel>
          </StatContent>
          <StatIcon $color="#ea580c">
            <Users size={24} />
          </StatIcon>
        </StatCard>
      </StatsGrid>

      <Toolbar>
        <ToolbarLeft>
          <SearchContainer>
            <SearchIcon>
              <Search size={16} />
            </SearchIcon>
            <SearchInput
              placeholder="Buscar por nome, descrição ou palavras-chave..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          <FilterContainer>
            <DropdownContainer data-dropdown>
              <CustomSelectButton
                type="button"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>
                  {filterType === 'all' ? 'Todos os tipos' :
                   filterType === 'image' ? 'Imagens' : 'Vídeos'}
                </span>
                <ChevronDown size={16} />
              </CustomSelectButton>
              {filterDropdownOpen && (
                <DropdownMenu>
                  <DropdownItem
                    $active={filterType === 'all'}
                    onClick={() => {
                      setFilterType('all');
                      setFilterDropdownOpen(false);
                    }}
                  >
                    Todos os tipos
                  </DropdownItem>
                  <DropdownItem
                    $active={filterType === 'image'}
                    onClick={() => {
                      setFilterType('image');
                      setFilterDropdownOpen(false);
                    }}
                  >
                    Imagens
                  </DropdownItem>
                  <DropdownItem
                    $active={filterType === 'video'}
                    onClick={() => {
                      setFilterType('video');
                      setFilterDropdownOpen(false);
                    }}
                  >
                    Vídeos
                  </DropdownItem>
                </DropdownMenu>
              )}
            </DropdownContainer>

            <ViewToggle>
              <ViewButton
                $active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                title="Visualização em grade"
              >
                <Grid size={16} />
              </ViewButton>
              <ViewButton
                $active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                title="Visualização em lista"
              >
                <List size={16} />
              </ViewButton>
            </ViewToggle>
          </FilterContainer>
        </ToolbarLeft>

        <ToolbarRight>
          <AddMediaButton
            onClick={() => canManageMedia && setShowUpload(true)}
            disabled={!canManageMedia}
          >
            <Plus size={16} />
            Adicionar Mídia
          </AddMediaButton>
        </ToolbarRight>
      </Toolbar>

      <ContentContainer>
        {filteredAndSortedMidias.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <Upload size={40} />
            </EmptyStateIcon>
            <EmptyStateTitle>
              {searchTerm || filterType !== 'all' ? 'Nenhuma mídia encontrada' : 'Nenhuma mídia configurada'}
            </EmptyStateTitle>
            <EmptyStateDescription>
              {searchTerm || filterType !== 'all'
                ? 'Tente ajustar os filtros de busca para encontrar as mídias que procura.'
                : 'Adicione imagens e vídeos para que o bot possa enviá-los automaticamente baseado em palavras-chave.'
              }
            </EmptyStateDescription>
            {!searchTerm && filterType === 'all' && (
              <Button
                variant="primary"
                onClick={() => setShowUpload(true)}
                style={{
                  background: '#FF2B2B',
                  border: 'none'
                }}
              >
                <Plus size={16} />
                Adicionar Primeira Mídia
              </Button>
            )}
          </EmptyState>
        ) : (
          <MediaGrid $viewMode={viewMode}>
            {filteredAndSortedMidias.map((media) => (
              <MediaCard
                key={media.fileName}
                media={media}
                onEdit={handleEditMedia}
                onDelete={handleDeleteMedia}
                onPreview={handlePreviewMedia}
              />
            ))}
          </MediaGrid>
        )}
      </ContentContainer>

      {/* Modal de Upload */}
      <AnimatePresence>
        {showUpload && (
          <MediaUpload
            userId={scopedUserId}
            onClose={() => setShowUpload(false)}
            onSuccess={() => {
              setShowUpload(false);
              reloadConfigurations();
            }}
          />
        )}
      </AnimatePresence>

      {/* Modal de Edição */}
      <AnimatePresence>
        {editingMedia && (
          <MediaEdit
            media={editingMedia}
            userId={scopedUserId}
            onClose={() => setEditingMedia(null)}
            onSuccess={() => {
              setEditingMedia(null);
              reloadConfigurations();
            }}
          />
        )}
      </AnimatePresence>

      {/* Modal de Exclusão */}
      <MediaDeleteModal
        media={deletingMedia}
        isOpen={!!deletingMedia}
        userId={scopedUserId}
        onClose={() => setDeletingMedia(null)}
        onConfirm={() => {
          setDeletingMedia(null);
          reloadConfigurations();
        }}
      />
    </PageContainer>
  );
};

export default Midias;